import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ResursTreeApi } from "../modules/tree";
import { useMemo } from "react";

interface ReqData extends ResursTreeApi.TCreateDeviceTreeSelect {}
interface ResData extends ResursTreeApi.TCreateDeviceTreeSelectResponse {}
const createDeviceTreeSelect = (data: ReqData) =>
  ResursTreeApi.createDeviceTreeSelect(data);

export function useDeviceTree() {
  const queryClient = useQueryClient();

  const { data: channels } = useQuery({
    queryKey: ["channels"],
    queryFn: (): ResData | null => null,
    refetchOnWindowFocus: false,
  });

  const { data: interfaces } = useQuery({
    queryKey: ["interfaces"],
    queryFn: (): ResData | null => null,
    refetchOnWindowFocus: false,
  });
  const { data: devices } = useQuery({
    queryKey: ["devices"],
    queryFn: (): ResData | null => null,
    refetchOnWindowFocus: false,
  });
  const { data: registrators } = useQuery({
    queryKey: ["registrators"],
    queryFn: (): ResData | null => null,
    refetchOnWindowFocus: false,
  });

  const { mutate: fetchChannels } = useMutation({
    mutationKey: ["fetch channels"],
    mutationFn: (data: ReqData) => createDeviceTreeSelect(data),
    onSuccess(newData) {
      queryClient.setQueryData<ResData>(["channels"], () => {
        return newData;
      });
    },
  });

  const { mutate: fetchInterfaces } = useMutation({
    mutationKey: ["fetch interfaces"],
    mutationFn: (data: ReqData) => createDeviceTreeSelect(data),
    onSuccess(newData) {
      queryClient.setQueryData<ResData>(["interfaces"], () => {
        return newData;
      });
    },
  });

  const { mutate: fetchDevices } = useMutation({
    mutationKey: ["fetch devices"],
    mutationFn: (data: ReqData) => createDeviceTreeSelect(data),
    onSuccess(newData) {
      queryClient.setQueryData<ResData>(["devices"], () => {
        return newData;
      });
    },
  });

  const { mutate: fetchRegistrators } = useMutation({
    mutationKey: ["fetch registrators"],
    mutationFn: (data: ReqData) => createDeviceTreeSelect(data),
    onSuccess(newData) {
      queryClient.setQueryData<ResData | null>(["registrators"], () => {
        if (newData.list.length === 0) return null;
        return newData;
      });
    },
  });

  const { mutate: clearRegistrators } = useMutation({
    mutationKey: ["clear registrators"],
    mutationFn: async () => null,
    onSuccess() {
      queryClient.setQueryData(["registrators"], () => {
        return null;
      });
    },
  });

  return useMemo(
    () => ({
      channels,
      interfaces,
      devices,
      registrators,
      fetchInterfaces,
      fetchDevices,
      fetchRegistrators,
      fetchChannels,
      clearRegistrators,
    }),
    [
      channels,
      interfaces,
      devices,
      registrators,
      fetchInterfaces,
      fetchDevices,
      fetchRegistrators,
      fetchChannels,
      clearRegistrators,
    ]
  );
}
